import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { Authenticator, useAuthenticator, ThemeProvider as AmplifyThemeProvider, createTheme as createAmplifyTheme } from "@aws-amplify/ui-react";
import Home from "./pages/Home";
import MenuBar from "./Components/MenuBar";
import "./App.css";
import Services from "./pages/Services";
import ClientsPage from "./pages/Clients";
import Settings from "./pages/Settings";
import ClientDetails from "./Components/ClientDetails";
import { SettingsRecord } from "./repositories/types";
import { SettingsContext } from "./contexts/SettingsContext";
import { DataStoreRepositoryProvider } from "./repositories";
import { DatastoreSettingsRepository } from "./repositories/datastore/SettingsRepository";
import ErrorBoundary from "./Components/ErrorBoundary";
import Invoices from "./pages/Invoices";
import Vets from "./pages/Vets";
import TaxReport from "./pages/Reports";
import { Hub } from "aws-amplify/utils";
import { DataStore } from "aws-amplify/datastore";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import "@aws-amplify/ui-react/styles.css";
import { ThemeProvider, Theme, StyledEngineProvider, createTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { indigo } from "@mui/material/colors";
import Subscription from "./pages/Subscription";
import { getCurrentUser } from "aws-amplify/auth";
import { isE2EMode } from "./e2eMode";
import { inMemoryRepositories } from "./repositories/inmemory";
export { SettingsContext };

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme { }
}


const theme = createTheme({
  palette: {
    primary: {
      main: indigo[500],
      light: "#6573c3",
      dark: "#2c387e",
    },
    secondary: {
      main: "#f50057",
      light: "#f73378",
      dark: "#ab003c",
    },
  },
});

const amplifyTheme = createAmplifyTheme({
  name: "pink-theme",
  tokens: {
    colors: {
      primary: {
        10: { value: "#fce4ec" },
        20: { value: "#f8bbd0" },
        40: { value: "#f48fb1" },
        60: { value: "#f73378" },
        80: { value: "#f50057" },
        90: { value: "#ab003c" },
        100: { value: "#6c0026" },
      },
    },
  },
});

type userDetails = {
  email: string;
  customerId: string;
}

function App() {
  const [settings, setSettings] = useState<SettingsRecord>();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const initialState = useMemo<"signUp" | "signIn">(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("signup") === "true" ? "signUp" : "signIn";
  }, []);
  const isAuthenticated = isE2EMode || authStatus === "authenticated";
  const [loading, setLoading] = useState(true);
  const [subscriptionDue, setSubscriptionDue] = useState(false);
  const [userDetails, setUserDetails] = useState<userDetails>();

  useEffect(() => {
    const unsubscribeAuth = Hub.listen("auth", async (data: any) => {
      if (data.payload.event === "signedOut") {
        await DataStore.clear();
      }
    });
    const unsubscribeDatastore = Hub.listen("datastore", async (data: any) => {
      if (data.payload.event === "ready") {
        if (settings === undefined || settings.id === "new") {
          updateSettings(await new DatastoreSettingsRepository().get());
        }
      }
    });
    return () => {
      unsubscribeAuth();
      unsubscribeDatastore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getInitialSettings = async () => {
      setLoading(true);
      if (isE2EMode) {
        updateSettings(await inMemoryRepositories.settings.get());
        setUserDetails({ email: "e2e@example.com", customerId: "e2e-customer" });
        return;
      }
      await DataStore.start();
      updateSettings(await new DatastoreSettingsRepository().get());
      //setSettings(await getSettings());
      const cId = await getCurrentUser();
      setUserDetails({
        email: cId.signInDetails?.loginId ?? "",
        customerId: cId.userId,
      });
    };
    if (isAuthenticated) {
      getInitialSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus]);

  useEffect(() => {
    if (settings && settings.id !== "new") {
      setLoading(false);
    }
  }, [settings])

  const updateSettings = (s: SettingsRecord) => {
    const due = new Date(s?.subscription?.validTo ?? "2025-01-01");
    const today = new Date();
    const subscribedCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("subscribed="))
      ?.split("=")[1];

    if (due < today && !subscribedCookie) {
      setSubscriptionDue(true);
    } else {
      setSubscriptionDue(false);
    }
    setSettings(s);
  };

  const formFields = {
    signUp: {
      email: {
        order: 1,
      },
      password: {
        order: 2,
      },
      confirm_password: {
        order: 3,
      },
    },
  };

  return (
    <div className="App">
      {isAuthenticated ? (
        loading ? (
          <CircularProgress />
        ) : !loading && subscriptionDue ? (
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <Subscription customerEmail={userDetails?.email ?? ""} customerId={userDetails?.customerId ?? ""}
                subscription={{
                  subscribed: settings?.subscription?.subscribed ?? false,
                  subscriptionActive: settings?.subscription?.subscriptionActive ?? false
                }}
              />
            </ThemeProvider>
          </StyledEngineProvider>
        ) : (
          <ErrorBoundary>
            <SettingsContext.Provider value={{ settings: settings, updateSettings: updateSettings }}>
              <DataStoreRepositoryProvider>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <BrowserRouter>
                        <MenuBar />
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/services" element={<Services />} />
                          <Route path="/clients" element={<ClientsPage />} />
                          <Route path="/clients/:id" element={<ClientDetails />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/invoices" element={<Invoices />} />
                          <Route path="/vets" element={<Vets />} />
                          <Route path="/reports" element={<TaxReport />} />
                        </Routes>
                      </BrowserRouter>
                    </LocalizationProvider>
                  </ThemeProvider>
                </StyledEngineProvider>
              </DataStoreRepositoryProvider>
            </SettingsContext.Provider>
          </ErrorBoundary>
        )
      ) : (
        <ThemeProvider theme={theme}>
          <AmplifyThemeProvider theme={amplifyTheme}>
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "grey.100" }}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  width: "100%",
                  maxWidth: 480,
                  borderRadius: 2,
                  "& [data-amplify-container]": { width: "100%", placeSelf: "stretch" },
                }}
              >
                <Authenticator
                  formFields={formFields}
                  initialState={initialState}
                  components={{
                    Header() {
                      return (
                        <Box sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="primary">
                            Simple Invoicing
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Manage your pet service business
                          </Typography>
                        </Box>
                      );
                    },
                  }}
                />
              </Paper>
            </Box>
          </AmplifyThemeProvider>
        </ThemeProvider>
      )}
    </div>
  );
}

export default App;
